import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuthStore } from './services/auth'
import { useEffect, lazy, Suspense } from 'react'
import { ThemeProvider } from './contexts/ThemeContext'
import { StripeProvider } from './contexts/StripeContext'
import { SocketProvider } from './contexts/SocketContext'
import { SidebarProvider, useSidebar } from './contexts/SidebarContext'
import { TestModeProvider } from './contexts/TestModeContext'
import { AuctionProvider, useAuctionContext } from './contexts/AuctionContext'
import { BidNotificationProvider } from './contexts/BidNotificationContext'
import { DonationNotificationProvider } from './contexts/DonationNotificationContext'
import { ToastProvider } from './contexts/ToastContext'
import { useDynamicFavicon } from './hooks/useDynamicFavicon'
import { useTrackingScripts } from './hooks/useTrackingScripts'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import ImpersonationBanner from './components/ImpersonationBanner'

/**
 * Pages are loaded per route rather than all up front.
 *
 * Importing every page eagerly meant one bundle containing the whole
 * application, so a bidder opening a public auction page also downloaded the
 * auctioneer tooling - GrapesJS for the page builder, the spreadsheet parser
 * for bulk upload, the Google Maps loader - none of which that visitor will
 * ever use. Each of these now becomes its own chunk, fetched on navigation.
 */
const HomePage = lazy(() => import('./pages/HomePage'))
const DashboardHomePage = lazy(() => import('./pages/DashboardHomePage'))
const SignInPage = lazy(() => import('./pages/SignInPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))

const AuctionsListPage = lazy(() => import('./pages/AuctionsListPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const CustomDomainDemo = lazy(() => import('./pages/CustomDomainDemo'))
const MyActivityPage = lazy(() => import('./pages/MyActivityPage'))
const MarketingPage = lazy(() => import('./pages/MarketingPage'))
const MarketingLibraryPage = lazy(() => import('./pages/MarketingLibraryPage'))

const AuctionPage = lazy(() => import('./pages/AuctionPage'))
const ItemPage = lazy(() => import('./pages/ItemPage'))
const OfferPage = lazy(() => import('./pages/OfferPage'))
const CreateAuctionPage = lazy(() => import('./pages/CreateAuctionPage'))
const EditAuctionPage = lazy(() => import('./pages/EditAuctionPage'))
const AddItemPage = lazy(() => import('./pages/AddItemPage'))
const EditItemPage = lazy(() => import('./pages/EditItemPage'))
const BulkUploadPage = lazy(() => import('./pages/BulkUploadPage'))
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'))
const AuctionAccessPage = lazy(() => import('./pages/AuctionAccessPage'))
const LiveAuctionPage = lazy(() => import('./pages/LiveAuctionPage'))
const LivePromoPage = lazy(() => import('./pages/LivePromoPage'))
const RafflePage = lazy(() => import('./pages/RafflePage'))
const RaffleItemPage = lazy(() => import('./pages/RaffleItemPage'))
const AddRaffleItemPage = lazy(() => import('./pages/AddRaffleItemPage'))
const EditRaffleItemPage = lazy(() => import('./pages/EditRaffleItemPage'))
const BulkUploadRaffleItemsPage = lazy(() => import('./pages/BulkUploadRaffleItemsPage'))
const DevTestPage = lazy(() => import('./pages/DevTestPage'))

/**
 * Paths the application itself owns.
 *
 * Auctions are served from the root as `/their-auction-slug`, so anything with
 * that shape which is NOT listed here is taken to be an auction and rendered
 * without the app's header and sidebar. That makes this list load-bearing: omit
 * a real page and it loses its navigation entirely rather than 404ing, which is
 * a confusing way to find out. Add new top-level routes here.
 */
const APP_ROUTES = [
  '/', '/signin', '/signup', '/onboarding', '/dashboard', '/auctions', '/my-activity',
  '/settings', '/users', '/transactions', '/notifications', '/create-auction',
  '/custom-domain-demo', '/marketing',
]

/**
 * App sections with their own sub-pages, e.g. /marketing/library. Without this
 * they would match the `/auction-slug/item-id` shape and lose their chrome for
 * the same reason.
 */
const APP_SECTIONS_WITH_SUBPAGES = ['/marketing']

/** Shown while a route's chunk is in flight. */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
      <span className="sr-only">Loading…</span>
    </div>
  )
}

// Component to redirect old auction URLs to simplified format
function RedirectToSimplified() {
  const { url } = useParams<{ url: string }>()
  return <Navigate to={`/${url}`} replace />
}

// Component to protect routes that require onboarding completion
function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  const location = useLocation()
  
  // If user is not logged in, redirect to signin with return URL
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/signin?returnUrl=${returnUrl}`} replace />
  }
  
  // If user hasn't completed onboarding, show the content but it will be blurred by the modal
  // The onboarding modal will appear as an overlay
  return <>{children}</>
}

function AppContent() {
  const { checkAuth, loading, user, impersonation } = useAuthStore()
  const { sidebarCollapsed, sidebarOpen, setSidebarOpen } = useSidebar()
  const { currentAuction } = useAuctionContext()
  const location = useLocation()
  
  // Set up dynamic favicon for custom domains
  useDynamicFavicon()

  // Run the auctioneer's own analytics/advertising pixels on their pages. On a
  // custom domain the server resolves the account from the host; on the shared
  // domain we hand it the account that owns the auction being viewed.
  useTrackingScripts(currentAuction?.account?.id)
  
  // Separate condition for promo page (no header/sidebar)
  const isPromoPage = location.pathname.endsWith('/promo')
  
  // Check if we're in preview mode (iframe preview from edit page)
  const isPreviewMode = new URLSearchParams(location.search).get('preview') === 'true'
  
  // Check if we're on a live auction page, item page, raffle page, or admin page (now using simplified routing)
  const isLiveAuctionPage = (
    // Admin pages (hide sidebar for admin functionality)
    location.pathname.startsWith('/admin/') ||
    // Main auction page: /auction-url
    (location.pathname.match(/^\/[^/]+$/) && !APP_ROUTES.includes(location.pathname)) ||
    // Item page within auction: /auction-url/item-id (exclude admin paths, promo,
    // and app sections that have pages of their own beneath them)
    (location.pathname.match(/^\/[^/]+\/[^/]+$/) && !location.pathname.startsWith('/admin/') && !location.pathname.endsWith('/promo') && !APP_SECTIONS_WITH_SUBPAGES.some(section => location.pathname.startsWith(`${section}/`))) ||
    // Raffle page: /auction/:auctionId/raffle/:raffleId
    location.pathname.match(/^\/auction\/[^/]+\/raffle\/[^/]+$/) ||
    // Raffle item page (admin): /auction/:auctionId/raffle/:raffleId/item/:itemId
    location.pathname.match(/^\/auction\/[^/]+\/raffle\/[^/]+\/item\/[^/]+$/) ||
    // Raffle item page (public): /:url/raffle/:raffleId/item/:itemId
    location.pathname.match(/^\/[^/]+\/raffle\/[^/]+\/item\/[^/]+$/)
  )
  
  // Debug logging for sidebar visibility
  // App routing debug info available in dev tools
  
  // Check if user needs onboarding
  const needsOnboarding = user && !user.onboardingCompleted

  useEffect(() => {
    checkAuth()
  }, [checkAuth])


  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const closeSidebar = () => {
    setSidebarOpen(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-primary">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Calculate the margin based on sidebar state
  const getMainMargin = () => {
    if (!user || isLiveAuctionPage || isPromoPage) return ''
    if (sidebarCollapsed) return 'md:ml-16'
    return 'md:ml-64'
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-primary">
      {/* Main App Content */}
      {!isPromoPage && !isPreviewMode && <ImpersonationBanner />}
      {user && !isPromoPage && !isLiveAuctionPage && <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />}
      {/* Reserve exactly the banner's measured height rather than a fixed 4rem,
          so the site sits flush under it even when its controls wrap. */}
      <div
        className={`${getMainMargin()} transition-all duration-300 ${needsOnboarding ? 'pointer-events-none opacity-50 blur-sm' : ''}`}
        style={
          impersonation?.isImpersonating && !isPreviewMode
            ? { paddingTop: 'var(--impersonation-banner-height)' }
            : undefined
        }
      >
        {!isPromoPage && <Header onMenuToggle={toggleSidebar} showMenuToggle={!isLiveAuctionPage} auction={currentAuction || undefined} />}
        <main className="flex-1">
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={user ? (
              <RequireOnboarding>
                <AuctionsListPage />
              </RequireOnboarding>
            ) : <HomePage />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/onboarding" element={<Navigate to="/auctions" replace />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/dashboard" element={user ? (
              <RequireOnboarding>
                <DashboardHomePage />
              </RequireOnboarding>
            ) : <Navigate to="/signin" replace />} />
            <Route path="/auctions" element={
              <RequireOnboarding>
                <AuctionsListPage />
              </RequireOnboarding>
            } />
            <Route path="/my-activity" element={
              <RequireOnboarding>
                <MyActivityPage />
              </RequireOnboarding>
            } />
            <Route path="/settings" element={
              <RequireOnboarding>
                <SettingsPage />
              </RequireOnboarding>
            } />
            {/* The section itself has no page of its own - it opens on Create. */}
            <Route path="/marketing" element={<Navigate to="/marketing/create" replace />} />
            <Route path="/marketing/create" element={
              <RequireOnboarding>
                <MarketingPage />
              </RequireOnboarding>
            } />
            <Route path="/marketing/library" element={
              <RequireOnboarding>
                <MarketingLibraryPage />
              </RequireOnboarding>
            } />
            <Route path="/users" element={
              <RequireOnboarding>
                <UsersPage />
              </RequireOnboarding>
            } />
            <Route path="/transactions" element={
              <RequireOnboarding>
                <TransactionsPage />
              </RequireOnboarding>
            } />
            <Route path="/notifications" element={
              <RequireOnboarding>
                <NotificationsPage />
              </RequireOnboarding>
            } />
            <Route path="/a/:id" element={
              <RequireOnboarding>
                <AuctionPage />
              </RequireOnboarding>
            } />
            <Route path="/offers/:token" element={
              <RequireOnboarding>
                <OfferPage />
              </RequireOnboarding>
            } />
            <Route path="/i/:id" element={
              <RequireOnboarding>
                <ItemPage />
              </RequireOnboarding>
            } />
            <Route path="/create-auction" element={
              <RequireOnboarding>
                <CreateAuctionPage />
              </RequireOnboarding>
            } />
            <Route path="/auctions/:id/edit" element={
              <RequireOnboarding>
                <EditAuctionPage />
              </RequireOnboarding>
            } />
            <Route path="/a/:id/new-item" element={
              <RequireOnboarding>
                <AddItemPage />
              </RequireOnboarding>
            } />
            <Route path="/a/:auctionId/edit-item/:itemId" element={
              <RequireOnboarding>
                <EditItemPage />
              </RequireOnboarding>
            } />
            <Route path="/auctions/:id/bulk-upload" element={
              <RequireOnboarding>
                <BulkUploadPage />
              </RequireOnboarding>
            } />
            <Route path="/auction/:url" element={<AuctionAccessPage />} />
            <Route path="/custom-domain-demo" element={<CustomDomainDemo />} />
            <Route path="/dev-test" element={<DevTestPage />} />
            {/* Raffle pages */}
            <Route path="/auction/:auctionId/raffle/:raffleId" element={<RafflePage />} />
            <Route path="/auction/:auctionId/raffle/:raffleId/item/:itemId" element={<RaffleItemPage />} />
            <Route path="/raffles/:raffleId/items/new" element={
              <RequireOnboarding>
                <AddRaffleItemPage />
              </RequireOnboarding>
            } />
            <Route path="/raffles/:raffleId/items/:itemId/edit" element={
              <RequireOnboarding>
                <EditRaffleItemPage />
              </RequireOnboarding>
            } />
            <Route path="/raffles/:raffleId/bulk-upload" element={
              <RequireOnboarding>
                <BulkUploadRaffleItemsPage />
              </RequireOnboarding>
            } />
            {/* Redirect old auction URLs to new simplified format */}
            <Route path="/auctions/:url" element={<RedirectToSimplified />} />
            <Route path="/live/:url" element={<RedirectToSimplified />} />
            {/* Live Promo Page - Full screen display for big screens */}
            <Route path="/:url/promo" element={<LivePromoPage />} />
            {/* Simplified auction routing - single route for all auctions */}
            <Route path="/:url" element={<LiveAuctionPage />} />
            <Route path="/:url/:itemId" element={<ItemPage />} />
            <Route path="/:url/raffle/:raffleId/item/:itemId" element={<RaffleItemPage />} />
          </Routes>
          </Suspense>
        </main>
      </div>

      {/* Onboarding Modal Overlay - appears over everything when needed */}
      {needsOnboarding && <Suspense fallback={null}><OnboardingPage /></Suspense>}
      

    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <StripeProvider>
        <SocketProvider>
          <ToastProvider>
            <BidNotificationProvider>
              <DonationNotificationProvider>
                <SidebarProvider>
                <TestModeProvider>
                  <AuctionProvider>
                    <AppContent />
                  </AuctionProvider>
                </TestModeProvider>
                </SidebarProvider>
              </DonationNotificationProvider>
            </BidNotificationProvider>
          </ToastProvider>
        </SocketProvider>
      </StripeProvider>
    </ThemeProvider>
  )
}

export default App